import React, { useMemo, useRef, useState } from 'react'
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { EstoqueItem } from '../../types/estoque'

type Props = {
  items: EstoqueItem[]
  value: string
  onChange: (itemId: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  required?: boolean
}

const EstoqueItemCombobox: React.FC<Props> = ({
  items,
  value,
  onChange,
  placeholder = 'Buscar por nome ou código...',
  disabled = false,
  className = '',
  required = false,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const selected = useMemo(() => items.find((item) => item.id === value) || null, [items, value])

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => {
      const name = item.name.toLowerCase()
      const code = String(item.code ?? '').toLowerCase()
      return name.includes(term) || code.includes(term)
    })
  }, [items, query])

  const handleSelect = (itemId: string) => {
    onChange(itemId)
    setQuery('')
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setQuery('')
    setOpen(false)
  }

  const handleFocus = () => {
    setQuery('')
    setOpen(true)
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false)
        setQuery('')
      }
    }, 150)
  }

  const inputDisplayValue = open
    ? query
    : selected
      ? `${selected.code} — ${selected.name}`
      : ''

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={inputDisplayValue}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={selected && !open ? undefined : placeholder}
          autoComplete="off"
          required={required && !value}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-16 text-sm text-gray-900 shadow-sm placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        />

        <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-1">
          {value && !disabled && (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Limpar item"
              tabIndex={-1}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              if (open) setOpen(false)
              else setOpen(true)
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronUpDownIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {open && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
          {filtered.length === 0 ? (
            <li className="select-none px-3 py-2 text-gray-400 dark:text-gray-500">
              Nenhum item encontrado.
            </li>
          ) : (
            filtered.map((item) => (
              <li
                key={item.id}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(item.id)
                }}
                className={`cursor-pointer select-none px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 ${
                  item.id === value
                    ? 'bg-primary-50/60 font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                <span className="text-gray-500 dark:text-gray-400">{item.code}</span>
                <span className="mx-1.5 text-gray-300 dark:text-gray-600">—</span>
                {item.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

export default EstoqueItemCombobox
